import { useTranslation } from 'react-i18next'
import { Trophy, BarChart3, ExternalLink } from 'lucide-react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'
import { RadarChart } from '@/components/v2/tools/amazon-listing/shared/RadarChart'

interface CompetitiveBenchmarkProps {
  benchmark: ListingAuditReport['competitive_benchmark']
  tld?: string
}

const DIM_KEYS: Record<string, string> = {
  title: 'dimTitle', bullets: 'dimBullets', images: 'dimImages',
  description: 'dimDescription', keywords: 'dimKeywords', reviews: 'dimReviews',
  pricing: 'dimPricing', compliance: 'dimCompliance', bsr: 'dimBsr',
}

function amazonUrl(asin: string, tld?: string): string {
  const domain = tld === 'com' ? 'amazon.com' : `amazon.${tld || 'de'}`
  return `https://www.${domain}/dp/${asin}`
}

export function CompetitiveBenchmark({ benchmark, tld }: CompetitiveBenchmarkProps) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.report.${key}`)
  if (!benchmark) return null
  const ma = benchmark.market_averages

  return (
    <div className="space-y-4">
      {benchmark.radar?.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
          <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">{r('competitiveBenchmark')}</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">{r('benchmarkIntro')}</p>
          <RadarChart data={benchmark.radar} />
          <DimensionPercentiles radar={benchmark.radar} r={r} />
        </div>
      )}

      {ma && <MarketAverages ma={ma} r={r} />}

      {benchmark.top_competitors?.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{r('topCompetitors')}</h4>
          {benchmark.top_competitors.map((comp, i) => (
            <CompetitorCard key={i} comp={comp} tld={tld} r={r} />
          ))}
        </div>
      )}
    </div>
  )
}

type Competitor = ListingAuditReport['competitive_benchmark']['top_competitors'][0]

function CompetitorCard({ comp, tld, r }: { comp: Competitor; tld?: string; r: (k: string) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <div className="flex items-start gap-2 mb-2">
        <Trophy className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-900 dark:text-white leading-snug">{comp.title}</p>
          <div className="flex items-center gap-2 mt-1">
            {comp.brand && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                {comp.brand}
              </span>
            )}
            <a
              href={amazonUrl(comp.asin, tld)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400 font-mono"
            >
              {comp.asin} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="space-y-1">
          <span className="text-2xs font-semibold text-green-600 uppercase">{r('competitorAdvantages')}</span>
          {comp.advantages?.map((a, j) => (
            <p key={j} className="text-xs text-neutral-600 dark:text-neutral-400">
              <span className="text-green-600 font-medium">+</span> {a}
            </p>
          ))}
        </div>
        <div className="space-y-1">
          <span className="text-2xs font-semibold text-red-600 uppercase">{r('competitorWeaknesses')}</span>
          {comp.weaknesses?.map((w, j) => (
            <p key={j} className="text-xs text-neutral-600 dark:text-neutral-400">
              <span className="text-red-600 font-medium">-</span> {w}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

function pctColor(pct: number): string {
  if (pct >= 70) return 'text-green-600 dark:text-green-400'
  if (pct >= 40) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function DimensionPercentiles({ radar, r }: { radar: ListingAuditReport['competitive_benchmark']['radar']; r: (k: string) => string }) {
  const hasPercentiles = radar.some((d) => d.percentile != null)
  if (!hasPercentiles) return null

  return (
    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
      {radar.map((dim) => {
        const labelKey = DIM_KEYS[dim.dimension]
        const label = labelKey ? r(labelKey) : dim.dimension
        return (
          <div key={dim.dimension} className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-2 text-center">
            <div className="text-2xs text-neutral-500 leading-tight mb-0.5">{label}</div>
            <div className={`text-sm font-bold ${pctColor(dim.percentile ?? 50)}`}>
              {r('betterThan')} {dim.percentile ?? 50}%
            </div>
            <div className="text-2xs text-neutral-400">{dim.source} {r('vs')} {dim.market_avg}</div>
          </div>
        )
      })}
    </div>
  )
}

function MarketAverages({ ma, r }: { ma: ListingAuditReport['competitive_benchmark']['market_averages']; r: (k: string) => string }) {
  const stats = [
    { label: r('maAvgTitleLength'), value: ma.title_length },
    { label: r('maAvgBulletCount'), value: ma.bullet_count },
    { label: r('maAvgImageCount'), value: ma.image_count },
    { label: r('maAvgRating'), value: ma.rating != null ? Number(ma.rating).toFixed(1) : '-' },
    { label: r('maAvgReviewCount'), value: ma.review_count },
    { label: r('maAvgPrice'), value: ma.price != null ? `${Number(ma.price).toFixed(2)}` : '-' },
  ]

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
        <BarChart3 className="w-4 h-4 text-neutral-500" /> {r('marketAverages')}
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {stats.map(({ label, value }) => (
          <div key={label} className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-2 text-center">
            <div className="text-2xs text-neutral-500 leading-tight mb-0.5">{label}</div>
            <div className="text-sm font-bold text-neutral-900 dark:text-white">{value ?? '-'}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
